import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchBlogPosts, fetchBlogPostByTitle } from '../contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { toUrlFriendly } from '../utils/urlHelpers';
import Breadcrumb from '../components/Breadcrumb';
import BlogCard from '../components/BlogCard';
import RecommendedProducts from '../components/RecommendedProducts';
import { styleCapitalizedText } from '../utils/textStyling';
import BlogHeader from '../components/Blog/BlogHeader';
import { processAndFormatContentfulData, paginateItems } from '../utils/contentful/contentfulHelpers';

// Rich text options for documentToReactComponents
const richTextOptions = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <p className="mb-6 text-gray-700 leading-relaxed text-lg">{children}</p>
    ),
    [BLOCKS.HEADING_1]: (node, children) => (
      <h1 className="text-5xl font-extrabold mb-8 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h1>
    ),
    [BLOCKS.HEADING_2]: (node, children) => (
      <h2 className="text-4xl font-bold mb-6 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h2>
    ),
    [BLOCKS.HEADING_3]: (node, children) => (
      <h3 className="text-4xl font-extrabold mb-8 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h3>
    ),
    [BLOCKS.HEADING_4]: (node, children) => (
      <h4 className="text-2xl font-bold mb-4 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h4>
    ),
    [BLOCKS.HEADING_5]: (node, children) => (
      <h5 className="text-xl font-bold mb-3 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h5>
    ),
    [BLOCKS.HEADING_6]: (node, children) => (
      <h6 className="text-lg font-bold mb-2 tracking-tight uppercase rugged-heading" style={{ fontFamily: "Geogrotesque, sans-serif" }}>{children}</h6>
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <ul className="list-disc ml-8 mb-6 space-y-2 text-gray-700">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (node, children) => (
      <ol className="list-decimal ml-8 mb-6 space-y-2 text-gray-700">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (node, children) => (
      <li className="mb-2 leading-relaxed">{children}</li>
    ),
    [BLOCKS.QUOTE]: (node, children) => (
      <blockquote className="border-l-4 border-[#D3242A] pl-6 py-2 my-8 italic text-gray-600 bg-gray-50 rounded-r-lg">
        {children}
      </blockquote>
    ),
    [BLOCKS.HR]: () => (
      <hr className="my-8 border-gray-200" />
    ),
    [BLOCKS.TABLE]: (node, children) => (
      <div className="overflow-x-auto my-8">
        <table className="min-w-full divide-y divide-gray-200">
          {children}
        </table>
      </div>
    ),
    [BLOCKS.TABLE_ROW]: (node, children) => (
      <tr className="bg-white">{children}</tr>
    ),
    [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
      <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        {children}
      </th>
    ),
    [BLOCKS.TABLE_CELL]: (node, children) => (
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 border-t border-gray-200">
        {children}
      </td>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { url, description, details } = node.data.target.fields.file;
      return (
        <div className="my-8">
          <img
            src={url}
            alt={description || ''}
            className="w-full rounded-lg shadow-lg"
            style={{
              maxHeight: details?.image?.height ? `${details.image.height}px` : 'auto',
              maxWidth: details?.image?.width ? `${details.image.width}px` : '100%'
            }}
          />
          {description && (
            <p className="text-sm text-gray-500 mt-2 text-center italic">{description}</p>
          )}
        </div>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      // Handle embedded entries (like embedded videos, tweets, etc.)
      const { contentType } = node.data.target.sys;
      if (contentType.sys.id === 'video') {
        const { url, title } = node.data.target.fields;
        return (
          <div className="my-8">
            <video
              src={url}
              controls
              className="w-full rounded-lg shadow-lg"
              title={title}
            />
          </div>
        );
      }
      return null;
    },
  },
  renderMark: {
    [MARKS.BOLD]: (text, node) => {
      // Check if the parent is a heading
      const isInHeading = node?.parent?.nodeType?.startsWith('heading');
      return (
        <strong className={isInHeading ? "!text-[#D3242A] font-extrabold" : "font-bold text-gray-900"}>
          {text}
        </strong>
      );
    },
    [MARKS.ITALIC]: (text) => (
      <em className="italic text-gray-800">{text}</em>
    ),
    [MARKS.UNDERLINE]: (text) => (
      <u className="underline decoration-[#D3242A] decoration-2">{text}</u>
    ),
    [MARKS.CODE]: (text) => (
      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono text-gray-800">{text}</code>
    ),
  },
};

const TipsAndArticles = () => {
  const { blogId, type } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  useEffect(() => {
    const fetchAndProcessData = async () => {
      try {
        console.log('Fetching blog posts...');
        const rawData = await fetchBlogPosts();
        console.log('Raw data:', rawData);
        
        if (!rawData || rawData.length === 0) {
          console.log('No data received from Contentful');
          setPosts([]);
          return;
        }

        const processedData = processAndFormatContentfulData(rawData);
        console.log('Processed data:', processedData);
        setPosts(processedData);
        setFilteredPosts(processedData);
      } catch (error) {
        console.error('Error processing data:', error);
        setPosts([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessData();
  }, []);

  useEffect(() => {
    const loadBlogPost = async () => {
      if (blogId) {
        try {
          setIsLoading(true);
          const post = await fetchBlogPostByTitle(blogId);
          if (!post) {
            console.error('Blog post not found:', blogId);
            navigate(`/${type}/tips-and-articles`);
            return;
          }
          setCurrentPost(post);
        } catch (error) {
          console.error('Error loading blog post:', error);
          navigate(`/${type}/tips-and-articles`);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadBlogPost();
  }, [blogId, type, navigate]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to the center of the viewport
    const viewportHeight = window.innerHeight;
    const scrollPosition = viewportHeight / 2;
    window.scrollTo({
      top: scrollPosition,
      behavior: 'smooth'
    });
  };

  const handleSearch = (results) => {
    setFilteredPosts(results);
    setCurrentPage(1); // Reset to first page when searching
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    },
    exit: {
      opacity: 0,
      transition: {
        staggerChildren: 0.1,
        staggerDirection: 1
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <BlogHeader />
        <div className="container mx-auto px-4 py-4 sm:py-8">
          <div className="max-w-7xl mx-auto py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mt-4 sm:mt-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {Array.from({ length: 6 }).map((_, index) => (
                <BlogCard key={index} post={null} />
              ))}
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  // If we have a blogId, show the individual blog post
  if (blogId) {
    if (!currentPost) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-xl mb-4">Article not found</div>
          <button
            onClick={() => navigate(`/${type}/tips-and-articles`)}
            className="bg-[#D3242A] text-white px-6 py-2 rounded-full hover:bg-red-700 transition-colors"
          >
            Back to Tips & Articles
          </button>
        </div>
      );
    }

    const {
      title,
      excerpt,
      pageHeaderImage,
      body,
      videoOrSecondaryImage,
      featuredImage,
      author,
      publishDate,
      tags
    } = currentPost.fields;

    const headerImageUrl = pageHeaderImage?.fields?.file?.url;
    const featuredImageUrl = featuredImage?.fields?.file?.url;
    const secondaryMediaUrl = videoOrSecondaryImage?.fields?.file?.url;

    return (
      <>
        <div className="container mx-auto px-4 py-4 sm:py-8">
          <Breadcrumb />
          <article className="max-w-4xl mx-auto py-6 sm:py-10 px-4 sm:px-6">
            <h1 
              className="text-3xl sm:text-5xl md:text-6xl font-extrabold mb-3 sm:mb-4 uppercase text-center text-[#D3242A]"
              style={{ fontFamily: "Geogrotesque, sans-serif" }}
            >
              {title}
            </h1>
            
            <div className="flex flex-col sm:flex-row items-center justify-center text-gray-500 text-xs sm:text-sm mb-4 sm:mb-6">
              {author && <span className="mb-1 sm:mb-0 sm:mr-4">By {author}</span>}
              {publishDate && (
                <span>{new Date(publishDate).toLocaleDateString()}</span>
              )}
            </div>

            {tags && tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
                {tags.map((tag, index) => (
                  <span
                    key={index}
                    className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs sm:text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {headerImageUrl && (
              <img
                src={headerImageUrl}
                alt={title}
                className="w-full h-[200px] sm:h-[400px] object-cover rounded-lg mb-6 sm:mb-8"
              />
            )}

            {excerpt && (
              <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 italic">{excerpt}</p>
            )}

            {featuredImageUrl && (
              <img
                src={featuredImageUrl}
                alt={title}
                className="w-full rounded-lg mb-6 sm:mb-8"
              />
            )}

            <div className="max-w-none prose prose-sm sm:prose-base lg:prose-lg">
              {styleCapitalizedText(documentToReactComponents(body, richTextOptions))}
            </div>

            {secondaryMediaUrl && (
              <div className="mt-6 sm:mt-8">
                {videoOrSecondaryImage.fields.file.contentType.startsWith('video/') ? (
                  <video
                    src={secondaryMediaUrl}
                    controls
                    className="w-full rounded-lg"
                  />
                ) : (
                  <img
                    src={secondaryMediaUrl}
                    alt="Secondary media"
                    className="w-full rounded-lg"
                  />
                )}
              </div>
            )}
          </article>
        </div>
        <RecommendedProducts type={type} />
      </>
    );
  }

  // Otherwise show the blog list with pagination
  const { items: paginatedPosts, pagination } = paginateItems(filteredPosts, currentPage, itemsPerPage);
  console.log('Current page:', currentPage);
  console.log('Paginated posts:', paginatedPosts);
  console.log('Pagination info:', pagination);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  if (!filteredPosts || filteredPosts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-xl mb-4">No articles found</div>
        <button
          onClick={() => navigate(`/${type}/tips-and-articles`)}
          className="bg-[#D3242A] text-white px-6 py-2 rounded-full hover:bg-red-700 transition-colors"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  return (
    <>
      <BlogHeader />
      <div className="container mx-auto px-4 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
          {/* Page Indicator */}
          <div className="text-center mb-8">
            <p className="text-gray-600">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, pagination.totalItems)} of {pagination.totalItems} articles
            </p>
          </div>

          {/* Top Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center space-x-6 mb-8">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pagination.hasPreviousPage
                    ? 'bg-[#D3242A] text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                } transition-colors`}
                aria-label="Previous page"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M15 19l-7-7 7-7" 
                  />
                </svg>
              </button>

              <div className="flex items-center space-x-4">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-all transform hover:scale-110 ${
                      currentPage === page
                        ? 'bg-[#D3242A] text-white scale-110'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pagination.hasNextPage
                    ? 'bg-[#D3242A] text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                } transition-colors`}
                aria-label="Next page"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 5l7 7-7 7" 
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Blog Cards Grid */}
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentPage}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mt-4 sm:mt-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {paginatedPosts.map((post) => (
                <BlogCard 
                  key={post.sys.id} 
                  post={post} 
                  type={type || 'household'} 
                />
              ))}
            </motion.div>
          </AnimatePresence>

          {/* Bottom Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center space-x-6 mt-8">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={!pagination.hasPreviousPage}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pagination.hasPreviousPage
                    ? 'bg-[#D3242A] text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                } transition-colors`}
                aria-label="Previous page"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M15 19l-7-7 7-7" 
                  />
                </svg>
              </button>

              <div className="flex items-center space-x-4">
                {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-medium transition-all transform hover:scale-110 ${
                      currentPage === page
                        ? 'bg-[#D3242A] text-white scale-110'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  pagination.hasNextPage
                    ? 'bg-[#D3242A] text-white hover:bg-red-700'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                } transition-colors`}
                aria-label="Next page"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-6 w-6" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M9 5l7 7-7 7" 
                  />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      <RecommendedProducts type={type} />
    </>
  );
};

export default TipsAndArticles;
